import { JSONWebToken } from '@parkingbase/ecommerce-api-demo';
import { logger } from '@/lib/logger';
import { isAccessTokenExpired } from '@/lib/token-expiry';

/**
 * Back-office "Login As User" support sessions.
 *
 * Held in sessionStorage (one tab) rather than the refresh-token cookie a normal
 * login uses, because a cookie is origin-wide — persisting a support session
 * there hands the impersonated customer's account to every other tab (PB-3817).
 */

export const IMPERSONATION_STORAGE_KEY = 'pb:impersonation-session';

function allowedParentOrigins(): string[] {
  return (process.env.NEXT_PUBLIC_IMPERSONATION_PARENT_ORIGINS ?? '')
    .split(',')
    .map((origin) => origin.trim())
    .filter(Boolean);
}

/** `ancestorOrigins` is Chrome/Safari only; `document.referrer` covers Firefox. */
function resolveTopLevelOrigin(): string | null {
  const ancestors = window.location.ancestorOrigins;
  if (ancestors && ancestors.length > 0) {
    return ancestors[ancestors.length - 1];
  }
  if (document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * ## Invariants:
 * - The `window.self !== window.top` check is load-bearing and never skipped:
 *   same-site iframes share their tab's storage partition, so a top-level tab
 *   can see the sessionStorage entry and must refuse it anyway. The origin
 *   allowlist is defence in depth, hence falling back to the frame check alone
 *   when it is unconfigured rather than breaking support tooling.
 */
export function isBackofficeFrame(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.self === window.top) return false;

  const allowed = allowedParentOrigins();
  if (allowed.length === 0) {
    logger.warn('Impersonation parent-origin allowlist is empty; frame check only', {
      feature: 'impersonation',
      route: 'isBackofficeFrame',
    });
    return true;
  }

  const topLevelOrigin = resolveTopLevelOrigin();
  return !!topLevelOrigin && allowed.includes(topLevelOrigin);
}

export function writeImpersonationSession(tokenDetails: JSONWebToken): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(tokenDetails));
  } catch (error) {
    logger.error('Failed to store impersonation session', error, {
      feature: 'impersonation',
      route: 'writeImpersonationSession',
    });
  }
}

export function clearImpersonationSession(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch (error) {
    logger.error('Failed to clear impersonation session', error, {
      feature: 'impersonation',
      route: 'clearImpersonationSession',
    });
  }
}

/**
 * ## Invariants:
 * - The frame and expiry checks live here, not at the call sites, so no caller
 *   can read the stored session without them. A top-level read also clears the
 *   entry: the support user navigated this tab away from back-office, so the
 *   session is over.
 */
export function readImpersonationSession(): JSONWebToken | null {
  if (typeof window === 'undefined') return null;

  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
  } catch (error) {
    logger.error('Failed to read impersonation session', error, {
      feature: 'impersonation',
      route: 'readImpersonationSession',
    });
    return null;
  }
  if (!raw) return null;

  if (!isBackofficeFrame()) {
    clearImpersonationSession();
    return null;
  }

  let tokenDetails: JSONWebToken;
  try {
    tokenDetails = JSON.parse(raw) as JSONWebToken;
  } catch (error) {
    logger.error('Impersonation session is not valid JSON', error, {
      feature: 'impersonation',
      route: 'readImpersonationSession',
    });
    clearImpersonationSession();
    return null;
  }

  if (!tokenDetails.access_token || isAccessTokenExpired(tokenDetails.access_token)) {
    clearImpersonationSession();
    return null;
  }

  return tokenDetails;
}
